import React from "react";
import { CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Loader, Upload, X } from "lucide-react";
import Image from "next/image";

interface StepFotoAutoProps {
    minNumberOfPhotos: number;
    getRootProps: any;
    getInputProps: any;
    isDragActive: boolean;
    photos: { file?: File | null; url: string | null; isLoading: boolean }[];
    removePhoto: (index: number) => void;
}

const StepFotoAuto: React.FC<StepFotoAutoProps> = ({
    minNumberOfPhotos,
    getRootProps,
    getInputProps,
    isDragActive,
    photos,
    removePhoto,
}) => {
    const arePhotosRequired = minNumberOfPhotos > 0;

    return (
        <div className="space-y-4">
            <CardDescription className="text-lg">
                Aggiungi foto della tua auto per una valutazione piu rapida e precisa
            </CardDescription>

            <Alert variant={arePhotosRequired ? "destructive" : "default"}>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{arePhotosRequired ? "Attenzione" : "Suggerimento"}</AlertTitle>
                <AlertDescription>
                    {arePhotosRequired
                        ? `Assicurati che le foto siano chiare e ben visibili. Il numero minimo di foto richieste e ${minNumberOfPhotos}.`
                        : "Assicurati che le foto siano chiare e ben visibili. Le foto non sono obbligatorie in questo flusso, ma aiutano a ottenere una valutazione piu rapida e precisa."}
                </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 gap-4">
                <div
                    {...getRootProps()}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                        isDragActive ? "border-primary bg-primary/10" : "border-gray-300 hover:border-primary"
                    }`}
                >
                    <input {...getInputProps()} />
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                        Trascina qui le tue foto o clicca per selezionarle
                    </p>
                    <p className="text-xs text-gray-500">
                        {arePhotosRequired
                            ? `(Minimo ${minNumberOfPhotos} foto, formati accettati: JPG, PNG)`
                            : "(Foto facoltative, formati accettati: JPG, PNG)"}
                    </p>
                </div>

                {photos.length > 0 && (
                    <div>
                        <h3 className="mb-3 text-lg font-semibold">
                            {arePhotosRequired
                                ? `Foto caricate (${photos.length}/${minNumberOfPhotos}):`
                                : `Foto caricate (${photos.length}):`}
                        </h3>
                        <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5">
                            {photos.map((photo, index) => (
                                <div key={index} className="group relative">
                                    <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-lg">
                                        {photo.isLoading ? (
                                            <Loader className="h-6 w-6 animate-spin text-gray-400" />
                                        ) : (
                                            <Image
                                                src={photo.url || ""}
                                                alt={`Foto auto ${index + 1}`}
                                                fill={true}
                                                sizes="(max-width: 640px) 33vw, (max-width: 1024px) 25vw, 20vw"
                                                className="object-cover"
                                            />
                                        )}
                                    </div>
                                    {!photo.isLoading && (
                                        <button
                                            onClick={() => removePhoto(index)}
                                            className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StepFotoAuto;
